import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { SERVER_URL } from "../config";
import axios from 'axios';
import TableHeaderFilter from './TableHeaderfilter';
import SearchableDropdown from './SearchableDropdown';
import FollowUpModal from "./FollowUpModal";

/* ── shared input style ─────────────────────────────────────────────────────── */
const inputStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-100";

const textareaStyle =
  "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors resize-none";

// Status options (canonical list)
const STATUS_OPTIONS = [
  "Open",
  "Closed",
  "Lost",
  "Successful (Payment Not Received)",
  "Successful & payment received",
];

// Statuses that require a receipt number
const RECEIPT_REQUIRED_STATUSES = [
  "Successful (Payment Not Received)",
  "Successful & payment received",
];

/* ════════════════════════════════════════════════════════════════════════════ */
const LeadTable = ({
  leads = [],
  totalEntries = 0,
  currentPage = 1,
  totalPages = 0,
  limit = 10,
  onPageChange = () => { },
  onLimitChange = () => { },
  searchQuery = '',
  onFilteredDataChange = () => { }
}) => {
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [newLeadModal, setNewLeadModal] = useState(false);
  const [viewLeadModal, setViewLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [productOptios, setProductOptions] = useState([]);
  const [serviceOptios, setServiceOptions] = useState([]);

  const [employeeNameFilter, setEmployeeNameFilter] = useState([]);
  const [productFilter, setProductFilter] = useState([]);
  const [serviceFilter, setServiceFilter] = useState([]);
  const [slNoFilter, setSlNoFilter] = useState([]);
  const [rateFilter, setRateFilter] = useState([]);
  const [quantityFilter, setQuantityFilter] = useState([]);
  const [companyFilter, setCompanyFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [branchFilter, setBranchFilter] = useState([]);
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);

  // Receipt number modal state
  const [receiptModal, setReceiptModal] = useState(false);
  const [receiptNumber, setReceiptNumber] = useState("");
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  const employee = JSON.parse(localStorage.getItem("employee")) || {
    employee_name: "",
    employee_id: ""
  };

  const [newLead, setNewLead] = useState({
    companyName: '',
    contactPerson: '',
    mobileNumber: '',
    alternateMobileNumber: '',
    branch: '',
    closed_amount: '',
    mailId: '',
    address: '',
    pinCode: '',
    product: '',
    service: '',
    usertype: '',
    remarks: '',
    status: 'Open',
    rate: '',
    quantity: '',
    slNo: '',
    leadCreatedDate: '',
    nextFollowUp: '',
    referenceName: '',
    commission_amount: '',
    expireDate: '',
    businessType: '',
    licenseType: ''
  });

  const formFields = [
    { name: 'usertype', label: 'User Type', type: 'select', placeholder: 'User Type', options: ['New', 'Existing'], colSpan: 1 },
    { name: 'slNo', label: 'Sl. No.(Non Editable)', type: 'text', placeholder: 'Serial Number', colSpan: 1 },
    { name: 'companyName', label: 'Company Name (Non Editable)', type: 'text', placeholder: 'Company Name', colSpan: 1 },
    { name: 'businessType', label: 'Business Type', type: 'select', placeholder: 'Select Business Type', options: 'businessType', colSpan: 1 },
    { name: 'contactPerson', label: 'Contact Person', type: 'text', placeholder: 'Contact Person', colSpan: 1 },
    { name: 'mobileNumber', label: 'Contact No', type: 'number', placeholder: 'Mobile Number', colSpan: 1 },
    { name: 'alternateMobileNumber', label: 'Alternate Mobile Number', type: 'number', placeholder: 'Enter Alternate Number', colSpan: 1 },
    { name: 'branch', label: 'Branch', type: 'select', placeholder: 'Select Branch', options: 'branches', colSpan: 1 },
    { name: 'mailId', label: 'Email ID', type: 'email', placeholder: 'Email ID', colSpan: 1 },
    { name: 'address', label: 'Address', type: 'text', placeholder: 'Address', colSpan: 1 },
    { name: 'pinCode', label: 'Pincode', type: 'text', placeholder: 'Pincode', colSpan: 1 },
    { name: 'product', label: 'Product (Non Editable)', type: 'select', placeholder: 'Select Product or Services', options: 'products', colSpan: 1 },
    { name: 'service', label: 'Service (Non Editable)', type: 'select', placeholder: 'Service Type', options: 'services', colSpan: 1 },
    { name: 'rate', label: 'Rate', type: 'number', placeholder: 'Enter Rate', colSpan: 1 },
    { name: 'qty', label: 'Quantity', type: 'number', placeholder: 'Enter Quantity', colSpan: 1 },
    { name: 'closedAmount', label: 'Closed Amount (Auto)', type: 'number', placeholder: '', colSpan: 1 },
    { name: 'referenceName', label: 'Reference Name', type: 'text', placeholder: 'Enter Reference Name', colSpan: 1 },
    { name: 'commission_amount', label: 'Commission Amount', type: 'number', placeholder: 'Enter Commission Amount', colSpan: 1 },
    { name: 'leadCreatedDate', label: 'Lead Creation Date', type: 'date', placeholder: '', colSpan: 1 },
    { name: 'nextFollowUp', label: 'Next Follow Up (Non Editable)', type: 'datetime-local', placeholder: '', colSpan: 1 },
    { name: 'expireDate', label: 'Expire Date', type: 'date', placeholder: '', colSpan: 1 },
    { name: 'status', label: 'Status', type: 'select', placeholder: 'Select Status', options: STATUS_OPTIONS, colSpan: 1 },
    { name: 'remarks', label: 'Remarks', type: 'text', placeholder: 'Remarks', colSpan: 1 }
  ];

  const [branchOptions, setBranchOptions] = useState([]);

  const fetchLeadBySlNo = async (slNo) => {
    if (!slNo?.trim()) return;

    try {
      const response = await axios.post(
        `${SERVER_URL}/Rahul/fetchLeadDetails.php`,
        { slNo }
      );

      if (response.data.status === "success") {
        const lead = response.data.lead;

        setNewLead(prev => ({
          ...prev,

          companyName: lead.companyName || "",
          contactPerson: lead.contactPerson || "",
          mobileNumber: lead.mobileNumber || "",
          alternateMobileNumber: lead.alternateMobileNumber || "",
          mailId: lead.mailId || "",
          address: lead.address || "",
          pinCode: lead.pinCode || "",
          product: lead.product || "",
          service: lead.service || "",
          branch: lead.branch || "",
          usertype: lead.usertype || "",
          rate: lead.rate || "",
          qty: lead.qty || "",
          referenceName: lead.referenceName || "",
          commission_amount: lead.commission_amount || "",
          expireDate: lead.expireDate || "",
          businessType: lead.businessType || "",
          licenseType: lead.licenseType || "",

          // DO NOT overwrite status
          status: "Open"
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchBusinessTypes = async () => {
    try {
      const res = await axios.get(`${SERVER_URL}/Rahul/getBusinessTypes.php`);
      if (res.data.status) setBusinessTypeOptions(res.data.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.post(`${SERVER_URL}/Rahul/branchFetch.php`, { employee_id: employee.employee_id });
      if (res.data.status) {
        const branches = res.data.branches || [];
        const userRegion = res.data.user_region || "";
        setBranchOptions(branches);
        const matchedBranch = branches.find(b => b.toLowerCase() === userRegion.toLowerCase());
        if (matchedBranch) setNewLead(prev => ({ ...prev, branch: matchedBranch }));
      }
    } catch (err) { console.error(err); }
  };

  const fetchProductDetails = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/DataFetch/productDetailsFetch.php`);
      if (response.data.status) assignProductDetails(response.data.productList);
    } catch { console.error('error'); }
  };

  const fetchLeads = async () => {
    try {
      const payload = { userName: employee.employee_name, userId: employee.employee_id };
      const response = await axios.post(`${SERVER_URL}/DataFetch/leadHistory.php`, payload, {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" }
      });
      if (response.data.status === "success") {
        setLeads(response.data.leads);
      }
    } catch (err) { console.error(err); }
  };

  const validateLead = (lead) => {
    const newErrors = {};
    const isCloud = lead.product?.toLowerCase() === "cloud";
    const isShared = lead.licenseType === "Shared License";
    const isExisting = lead.usertype === "Existing";

    if (isExisting && !(isCloud && isShared)) {
      if (!lead.slNo) newErrors.slNo = true;
    }

    if (!lead.companyName) newErrors.companyName = true;
    if (!lead.contactPerson) newErrors.contactPerson = true;
    if (!lead.mobileNumber) newErrors.mobileNumber = true;
    if (!lead.branch) newErrors.branch = true;
    if (!lead.address) newErrors.address = true;
    if (!lead.usertype) newErrors.usertype = true;

    if (isCloud && !lead.licenseType) newErrors.licenseType = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchProductDetails();
    fetchBranches();
    fetchBusinessTypes();
  }, []);

  useEffect(() => {
    const rate = Number(newLead.rate || 0);
    const qty = Number(newLead.qty || newLead.quantity || 0);
    const commission = Number(newLead.commission_amount || 0);
    let amount = qty > 0 ? (rate * qty) - commission : rate - commission;
    if (amount < 0) amount = 0;
    setNewLead(prev => ({ ...prev, closedAmount: amount }));
  }, [newLead.rate, newLead.qty, newLead.quantity, newLead.commission_amount]);

  useEffect(() => {
    if (!selectedLead) return;
    const rate = Number(selectedLead.rate || 0);
    const qty = Number(selectedLead.qty || 0);
    const commission = Number(selectedLead.commission_amount || 0);
    let amount = qty > 0 ? (rate * qty) - commission : rate - commission;
    if (amount < 0) amount = 0;
    setSelectedLead(prev => ({ ...prev, closedAmount: amount }));
  }, [selectedLead?.rate, selectedLead?.qty, selectedLead?.commission_amount]);

  const toDropdownOptions = (arr = []) => arr.map(v => ({ label: v, value: v }));
  const uniqueOptions = (arr) => [...new Set(arr)].map(v => ({ label: v, value: v }));

  const employeeNameOptions = useMemo(() => uniqueOptions(leads.map(l => l.username).filter(Boolean)), [leads]);
  const companyOptions = useMemo(() => uniqueOptions(leads.map(l => l.companyName).filter(Boolean)), [leads]);
  const productOptions = useMemo(() => uniqueOptions(leads.map(l => l.product).filter(Boolean)), [leads]);
  const serviceOptions = useMemo(() => uniqueOptions(leads.map(l => l.service).filter(Boolean)), [leads]);
  const slNoOptions = useMemo(() => uniqueOptions(leads.map(l => l.slNo).filter(Boolean)), [leads]);
  const rateOptions = useMemo(() => uniqueOptions(leads.map(l => l.rate).filter(v => v !== undefined && v !== null)), [leads]);
  const branchOptionsFilter = useMemo(() => {
    const map = new Map();
    leads.forEach(l => { if (l.branch) { const key = l.branch.toLowerCase(); if (!map.has(key)) map.set(key, l.branch); } });
    return Array.from(map.values()).map(v => ({ label: v, value: v.toLowerCase() }));
  }, [leads]);
  const quantityOptions = useMemo(() => uniqueOptions(leads.map(l => l.quantity).filter(v => v !== undefined && v !== null)), [leads]);
  const statusOptions = useMemo(() => STATUS_OPTIONS.map(v => ({ label: v, value: v })), []);

  const assignProductDetails = (list) => {
    let productList = [], serviceList = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].category === 'TallyProduct') productList.push(list[i].product_name);
      else if (list[i].category === 'Service') serviceList.push(list[i].product_name);
      setProductOptions(productList);
      setServiceOptions(serviceList);
    }
  };

  useEffect(() => { setSearchTerm(searchQuery); }, [searchQuery]);

  const saveLead = async (newLead, setNewLead, employee) => {
    if (!validateLead(newLead)) { alert("Required fields are missing."); return; }
    try {
      const formPayload = formFields.reduce((acc, field) => { acc[field.name] = newLead[field.name] ?? ""; return acc; }, {});
      const response = await fetch(`${SERVER_URL}/DataInsert/insertNewLead.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formPayload, employee: employee })
      });
      const result = await response.json();
      if (result.status === "success") {
        const resetLead = formFields.reduce((acc, field) => { acc[field.name] = ""; return acc; }, { status: "Open" });
        setNewLead(resetLead);
        alert("Lead Created.");
        fetchLeads();
        setNewLeadModal(false);
      } else { alert(result.message || "Failed to create lead."); }
    } catch (error) { console.error("Lead Insert Error:", error); alert("Network error. Please try again."); }
  };

  // ── Handle status change with receipt number gate ──
  const handleStatusChange = (newStatus, currentData, setter) => {
    if (RECEIPT_REQUIRED_STATUSES.includes(newStatus)) {
      setPendingStatusChange({ newStatus, currentData, setter });
      setReceiptNumber("");
      setReceiptModal(true);
    } else {
      setter(prev => ({ ...prev, status: newStatus, receiptNumber: "" }));
    }
  };

  const confirmReceiptAndApplyStatus = () => {
    if (!receiptNumber.trim()) {
      alert("Receipt number is required for this status.");
      return;
    }
    if (pendingStatusChange) {
      const { newStatus, setter } = pendingStatusChange;
      setter(prev => ({ ...prev, status: newStatus, receiptNumber: receiptNumber.trim() }));
    }
    setReceiptModal(false);
    setPendingStatusChange(null);
    setReceiptNumber("");
  };

  const updateLead = async (leadData) => {
    try {
      const response = await fetch(`${SERVER_URL}/DataUpdate/leadUpdate.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...leadData, leadId: leadData.lead_id, employee: employee })
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const result = await response.json();
      if (result.status === "success") {
        alert("Lead Updated Successfully.");
        setIsEditing(false);
        setViewLeadModal(false);
        fetchLeads();
      } else { alert(result.message || "Failed to update lead."); }
    } catch (error) { console.error("Lead Update Error:", error); alert("Error updating lead."); }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch =
      (lead.companyName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (lead.contactPerson?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (lead.mobileNumber?.toString() || "").includes(searchTerm) ||
      (lead.username?.toLowerCase() || "").includes(searchTerm.toLowerCase());
    const matchEmployeeName = employeeNameFilter.length === 0 || employeeNameFilter.includes(lead.username);
    const matchBranch = branchFilter.length === 0 || branchFilter.includes((lead.branch || "").toLowerCase());
    const matchCompany = companyFilter.length === 0 || companyFilter.includes(lead.companyName);
    const matchProduct = productFilter.length === 0 || productFilter.includes(lead.product);
    const matchService = serviceFilter.length === 0 || serviceFilter.includes(lead.service);
    const matchSlNo = slNoFilter.length === 0 || slNoFilter.includes(lead.slNo);
    const matchRate = rateFilter.length === 0 || rateFilter.includes(lead.rate);
    const matchQuantity = quantityFilter.length === 0 || quantityFilter.includes(lead.quantity);
    const matchStatus = statusFilter.length === 0 || statusFilter.includes(lead.prospect);
    return matchesSearch && matchEmployeeName && matchCompany && matchProduct && matchService && matchSlNo && matchRate && matchQuantity && matchBranch && matchStatus;
  });

  useEffect(() => { onFilteredDataChange(filteredLeads); }, [filteredLeads]);

  const handleSearchSubmit = (e) => { e.preventDefault(); };
  const handleNewLeadChange = (e) => {
    const { name, value } = e.target;
    if (name === "status") {
      handleStatusChange(value, newLead, setNewLead);
    } else {
      setNewLead(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleRowClick = (lead) => {
    const leadData = {};
    formFields.forEach(field => {
      if (field.name === "status") leadData.status = lead.prospect ?? "";
      else if (field.name === "qty") leadData.qty = lead.qty ?? lead.quantity ?? "";
      else if (field.name === "branch") leadData.branch = (lead.branch || "").toLowerCase();
      else leadData[field.name] = lead[field.name] ?? "";
    });
    setSelectedLead({ ...lead, ...leadData });
    setViewLeadModal(true);
    setIsEditing(false);
  };

  const handleEditClick = () => { setIsEditing(true); };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "status") {
      handleStatusChange(value, selectedLead, setSelectedLead);
    } else {
      setSelectedLead(prev => ({ ...prev, [name]: name === "branch" ? value.toLowerCase() : value }));
    }
  };
  const handleSaveEdit = () => { updateLead(selectedLead); };

  const getFieldOptions = (field) => {
    if (field.name === 'product') return toDropdownOptions(productOptios);
    if (field.name === 'service') return toDropdownOptions(serviceOptios);
    if (field.name === 'businessType') return businessTypeOptions.map(item => ({ label: item.name, value: item.name }));
    if (field.name === 'status') return toDropdownOptions(STATUS_OPTIONS);
    if (field.name === 'usertype') return toDropdownOptions(field.options || []);
    if (field.name === 'branch') return branchOptions.map(b => ({ label: b, value: b.toLowerCase() }));
    return [];
  };

  const nonEditableFields = ["slNo", "companyName", "product", "service", "nextFollowUp", "leadCreatedDate"];

  const renderFormField = (field, value, onChange, disabled = false) => {
    const fieldValue = value || "";
    const hasError = errors[field.name];

    if (field.type === "select") {
      return (
        <>
          <SearchableDropdown
            label=""
            options={getFieldOptions(field)}
            value={fieldValue}
            placeholder={field.placeholder}
            disabled={disabled}
            onChange={(val) => onChange({ target: { name: field.name, value: val } })}
          />
          {hasError && <p className="text-rose-500 text-xs mt-1">Required field is not filled</p>}
        </>
      );
    }

    return (
      <>
        <input
          className={`${inputStyle} ${hasError ? 'border-rose-400 focus:ring-rose-400' : ''}`}
          type={field.type}
          placeholder={field.placeholder}
          name={field.name}
          value={fieldValue}
          onChange={onChange}
          disabled={disabled}

          onKeyDown={(e) => {
            if (field.name === "slNo" && e.key === "Enter") {
              fetchLeadBySlNo(e.target.value);
            }
          }}

          onBlur={(e) => {
            if (field.name === "slNo") {
              fetchLeadBySlNo(e.target.value);
            }
          }}
        />
        {hasError && <p className="text-rose-500 text-xs mt-1">Required field is not filled</p>}
      </>
    );
  };

  // Status badge color
  const statusBadge = (status) => {
    const map = {
      "Open": "bg-blue-100 text-blue-700",
      "Closed": "bg-gray-200 text-gray-600",
      "Lost": "bg-red-100 text-red-600",
      "Successful (Payment Not Received)": "bg-amber-100 text-amber-700",
      "Successful & payment received": "bg-green-100 text-green-700",
    };
    return map[status] || "bg-slate-100 text-slate-500";
  };

  const startEntry = filteredLeads.length > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endEntry = Math.min(currentPage * limit, filteredLeads.length);
  const totalPagesCalculated = Math.ceil(filteredLeads.length / limit);

  /* ════════════════════════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="flex flex-col h-screen" style={{ background: "#f0f4f8" }}>

        {/* ── STICKY HEADER ─────────────────────────────────────────── */}
        <div
          className="sticky top-0 z-20 border-b border-slate-200 px-6 py-3"
          style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
              <h2 className="text-lg font-bold tracking-tight text-slate-800">Leads Activity</h2>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex w-full sm:w-[300px]">
                <input
                  type="text"
                  placeholder="Search by name, phone or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit(e)}
                  className="w-full rounded-l-lg bg-slate-50 border border-slate-200 px-4 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <button
                  onClick={handleSearchSubmit}
                  className="px-3 rounded-r-lg text-white transition-colors"
                  style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Show</label>
                <select
                  value={limit}
                  onChange={(e) => onLimitChange(Number(e.target.value))}
                  className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs text-slate-500">entries</span>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setNewLeadModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Lead
                </button>
                <button
                  onClick={() => setFollowUpModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-blue-700 border border-blue-200 bg-blue-50 transition-all hover:bg-blue-100"
                >
                  Follow Up
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── TABLE ─────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden p-5">
          <div className="rounded-xl overflow-hidden h-full flex flex-col bg-white border border-slate-200" style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
            <div className="flex-1 overflow-y-auto overflow-x-auto att-scrollbar">
              <table className="min-w-[1250px] w-full text-sm text-left border-collapse">
                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                  <tr>
                    {[
                      { label: "Sl. No.", filter: <TableHeaderFilter label="Sl. No." options={slNoOptions} selectedValues={slNoFilter} onChange={setSlNoFilter} /> },
                      { label: "Employee Name", filter: <TableHeaderFilter label="Employee Name" options={employeeNameOptions} selectedValues={employeeNameFilter} onChange={setEmployeeNameFilter} /> },
                      { label: "Branch", filter: <TableHeaderFilter label="Branch" options={branchOptionsFilter} selectedValues={branchFilter} onChange={setBranchFilter} /> },
                      { label: "Creation Date", filter: <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Creation Date</span> },
                      { label: "Company Name", filter: <TableHeaderFilter label="Company Name" options={companyOptions} selectedValues={companyFilter} onChange={setCompanyFilter} /> },
                      { label: "Tally Product", filter: <TableHeaderFilter label="Tally Product" options={productOptions} selectedValues={productFilter} onChange={setProductFilter} /> },
                      { label: "Service", filter: <TableHeaderFilter label="Service" options={serviceOptions} selectedValues={serviceFilter} onChange={setServiceFilter} /> },
                      { label: "Rate", filter: <TableHeaderFilter label="Rate" options={rateOptions} selectedValues={rateFilter} onChange={setRateFilter} /> },
                      { label: "Quantity", filter: <TableHeaderFilter label="Quantity" options={quantityOptions} selectedValues={quantityFilter} onChange={setQuantityFilter} /> },
                      {
                        label: "Status",
                        filter: (
                          <TableHeaderFilter
                            label="Status"
                            options={statusOptions}
                            selectedValues={statusFilter}
                            onChange={setStatusFilter}
                          />
                        )
                      },
                      {
                        label: "Status Update Date",
                        filter: (
                          <TableHeaderFilter
                            label="Status Update Date"
                            options={[]}
                            selectedValues={[]}
                            onChange={() => { }}
                          />
                        )
                      },
                      {
                        label: "Next Follow Up",
                        filter: (
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Next Follow Up
                          </span>
                        )
                      },
                    ].map((col, i) => (
                      <th key={i} className="py-3 px-4 text-left border-b border-slate-200">
                        {col.filter}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {filteredLeads.length ? (
                    filteredLeads
                      .slice((currentPage - 1) * limit, currentPage * limit)
                      .map((lead, idx) => (
                        <tr
                          key={lead.leadId ?? idx}
                          className="border-t border-slate-100 cursor-pointer transition-colors hover:bg-blue-50/60"
                          onClick={() => handleRowClick(lead)}
                        >
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[140px]">{lead.slNo}</td>
                          <td className="py-3 px-4 text-slate-800 text-xs font-medium whitespace-nowrap">{lead.username}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">{lead.branch}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">{lead.leadCreatedDate}</td>
                          <td className="py-3 px-4 text-slate-700 text-xs truncate max-w-[220px]">{lead.companyName}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[160px]">{lead.product}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[160px]">{lead.service}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs truncate max-w-[140px]">{lead.rate}</td>
                          <td className="py-3 px-4 text-slate-500 text-xs text-center truncate max-w-[140px]">{lead.quantity || lead.qty}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${statusBadge(lead.prospect)}`}>
                              {lead.prospect || "—"}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-xs whitespace-nowrap">
                            {lead["statusUpdateTime"] || "—"}
                          </td>
                          <td className="py-3 px-4 text-slate-400 text-xs whitespace-nowrap">{lead.nextFollowUp}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-slate-400 text-sm">
                        No leads found with current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ── PAGINATION FOOTER ─────────────────────────────── */}
        <div
          className="sticky bottom-0 z-10 border-t border-slate-200 px-6 py-3"
          style={{ background: "#ffffff", boxShadow: "0 -1px 4px rgba(15,23,42,0.06)" }}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
            <div className="text-xs text-slate-500 font-medium">
              Showing <span className="text-slate-700 font-bold">{startEntry}</span> to{' '}
              <span className="text-slate-700 font-bold">{endEntry}</span> of{' '}
              <span className="text-slate-700 font-bold">{filteredLeads.length}</span> entries
            </div>
            <div className="inline-flex items-center gap-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>
              <div className="px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-600 font-semibold">
                Page {currentPage} of {totalPagesCalculated || 1}
              </div>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPagesCalculated}
                className="p-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── RECEIPT NUMBER MODAL ─────────────────────────────────────── */}
      {receiptModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(6px)" }}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm border border-slate-200" style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.2)" }}>
            <h3 className="text-base font-bold text-slate-800 mb-1">Receipt Number Required</h3>
            <p className="text-xs text-slate-500 mb-4">
              To mark this lead as <span className="font-semibold text-slate-700">"{pendingStatusChange?.newStatus}"</span>, please provide a receipt number.
            </p>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Receipt Number <span className="text-rose-500">*</span></label>
            <input
              className={inputStyle}
              placeholder="Enter receipt number"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value)}
            />
            <div className="flex gap-3 mt-5">
              <button
                onClick={confirmReceiptAndApplyStatus}
                disabled={!receiptNumber.trim()}
                className="flex-1 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 transition-all"
                style={{ background: "linear-gradient(135deg, #059669, #10b981)" }}
              >
                Confirm
              </button>
              <button
                onClick={() => { setReceiptModal(false); setPendingStatusChange(null); setReceiptNumber(""); }}
                className="flex-1 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD NEW LEAD MODAL ──────────────────────────────────────── */}
      {newLeadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6 overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <PlusIcon className="h-5 w-5 text-blue-600" />
                  Add New Lead
                </h3>
              </div>
              <button onClick={() => setNewLeadModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {formFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {["slNo", "companyName", "contactPerson", "mobileNumber", "branch", "address", "usertype"].includes(field.name) && (
                      <span className="text-rose-500 ml-1">*</span>
                    )}
                  </label>
                  {renderFormField(field, newLead[field.name], handleNewLeadChange)}

                  {field.name === "product" && newLead.product?.toLowerCase() === "cloud" && (
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">License Type</label>
                      <SearchableDropdown
                        label=""
                        options={[
                          { label: "Shared License", value: "Shared License" },
                          { label: "Client License", value: "Client License" }
                        ]}
                        value={newLead.licenseType}
                        placeholder="Select License Type"
                        onChange={(val) => handleNewLeadChange({ target: { name: "licenseType", value: val } })}
                      />
                      {errors.licenseType && <p className="text-rose-500 text-xs mt-1">Required field</p>}
                    </div>
                  )}

                  {/* Show receipt number field inline if status requires it and it's set */}
                  {field.name === "status" && RECEIPT_REQUIRED_STATUSES.includes(newLead.status) && newLead.receiptNumber && (
                    <div className="mt-2">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Receipt Number</label>
                      <input className={inputStyle} value={newLead.receiptNumber} readOnly />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setNewLeadModal(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">Cancel</button>
              <button
                onClick={() => saveLead(newLead, setNewLead, employee)}
                className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
              >
                Save Lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW / EDIT LEAD MODAL ──────────────────────────────────── */}
      {viewLeadModal && selectedLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)" }}>
          <div
            className="w-full max-w-3xl rounded-2xl bg-white border border-slate-200 p-6 overflow-y-auto max-h-[90vh] att-scrollbar"
            style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.18)" }}
          >
            <div className="flex justify-between items-center mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  {isEditing ? <><PencilIcon className="h-5 w-5 text-blue-600" /> Edit Lead</> : 'Lead Details'}
                </h3>
              </div>
              <div className="flex gap-2">
                {!isEditing && (
                  <button
                    onClick={handleEditClick}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)", boxShadow: "0 2px 8px rgba(30,64,175,0.18)" }}
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                )}
                <button onClick={() => setViewLeadModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {formFields.map((field) => (
                <div key={field.name} className={field.colSpan === 2 ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                    {field.label}
                    {["slNo", "companyName", "contactPerson", "mobileNumber", "branch", "address", "usertype"].includes(field.name) && (
                      <span className="text-rose-500 ml-1">*</span>
                    )}
                  </label>
                  {renderFormField(field, selectedLead[field.name], handleEditChange, !isEditing || nonEditableFields.includes(field.name))}

                  {field.name === "product" && selectedLead.product?.toLowerCase() === "cloud" && (
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">License Type</label>
                      <SearchableDropdown
                        label=""
                        options={[
                          { label: "Shared License", value: "Shared License" },
                          { label: "Client License", value: "Client License" }
                        ]}
                        value={selectedLead.licenseType}
                        disabled={!isEditing}
                        placeholder="Select License Type"
                        onChange={(val) => handleEditChange({ target: { name: "licenseType", value: val } })}
                      />
                      {errors.licenseType && <p className="text-rose-500 text-xs mt-1">Required field</p>}
                    </div>
                  )}

                  {field.name === "status" && RECEIPT_REQUIRED_STATUSES.includes(selectedLead.status) && selectedLead.receiptNumber && (
                    <div className="mt-2">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Receipt Number</label>
                      <input className={inputStyle} value={selectedLead.receiptNumber} readOnly />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setViewLeadModal(false)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors">Close</button>
              {isEditing && (
                <button
                  onClick={handleSaveEdit}
                  className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 2px 8px rgba(5,150,105,0.18)" }}
                >
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {followUpModalOpen && (
        <FollowUpModal
          isOpen={followUpModalOpen}
          onClose={() => setFollowUpModalOpen(false)}
          onRefresh={fetchLeads}
        />
      )}
    </>
  );
};

export default LeadTable;